'use client';

import { logger } from '@adornis/base/logging.js';
import { ArrowLeft, BookOpen, Check, Clock, GraduationCap } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getMyClassExamResultsQuery,
  type StudentClassExamResultDTO,
} from '../../../db/api/get-my-class-exam-results.js';
import type { ClassRoom } from '../../../db/classroom.js';
import { isSubject } from '../../../db/subjects.js';
import { Button } from '../ui/button.js';
import { StudentExamResultPage } from './student-exam-result-page.js';

interface StudentClassPageProps {
  classroom: ClassRoom;
  onBack: () => void;
}

/**
 * A student's view of one of their classes: their own results for its exams.
 *
 * Deliberately built from the same markup as the teacher's `ClassRoster` (classroom-page.tsx) — same header,
 * same stat tiles, same exam rows — so the two sides of the app read as one product. What differs is only
 * what a student is allowed to see: their own grade per exam instead of the class average, and no roster,
 * join code or pending requests.
 */
export function StudentClassPage({ classroom, onBack }: StudentClassPageProps) {
  const { t, i18n } = useTranslation();

  const [exams, setExams] = useState<StudentClassExamResultDTO[]>([]);
  const [averageGrade, setAverageGrade] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [openExam, setOpenExam] = useState<StudentClassExamResultDTO | null>(null);

  useEffect(() => {
    if (!classroom._id) return;

    let isMounted = true;
    setIsLoading(true);

    void getMyClassExamResultsQuery(classroom._id)({
      averageGrade: 1,
      exams: { examId: 1, title: 1, subject: 1, createdAt: 1, grade: 1, percentage: 1, points: 1, maxPoints: 1 },
    })
      .then(result => {
        if (!isMounted) return;
        setExams(result?.exams ?? []);
        setAverageGrade(result?.averageGrade ?? null);
      })
      .catch(err => logger.error(err, 'Failed to load class results'))
      .finally(() => {
        if (isMounted) setIsLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [classroom._id]);

  const gradedExams = useMemo(() => exams.filter(exam => exam.grade != null), [exams]);
  const averagePercentage = useMemo(() => {
    const values = gradedExams.map(e => e.percentage).filter((p): p is number => p != null);
    if (values.length === 0) return null;
    return Math.round((values.reduce((sum, p) => sum + p, 0) / values.length) * 10) / 10;
  }, [gradedExams]);

  const subjectLabel = (subject?: string | null) =>
    subject && isSubject(subject) ? t(`Subjects.${subject}`) : subject || '';

  const formatDate = (value?: string | null) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return new Intl.DateTimeFormat(i18n.language, { day: '2-digit', month: '2-digit', year: 'numeric' }).format(date);
  };

  const stats = [
    { key: 'averageGrade', Icon: Check, value: averageGrade != null ? averageGrade.toFixed(1) : '—' },
    { key: 'examCount', Icon: BookOpen, value: String(gradedExams.length) },
    {
      key: 'averagePercent',
      Icon: GraduationCap,
      value: averagePercentage != null ? `${averagePercentage.toFixed(0)}%` : '—',
    },
  ] as const;

  if (openExam) {
    return (
      <StudentExamResultPage
        examId={openExam.examId}
        examTitle={openExam.title}
        onBack={() => setOpenExam(null)}
      />
    );
  }

  return (
    <div className="flex flex-col h-full w-full bg-background overflow-y-auto">
      <div className="sticky top-0 z-10 border-b border-border bg-card px-4 sm:px-8 py-2 sm:py-4 shadow-sm">
        <div className="flex items-center gap-2 max-w-6xl mx-auto">
          <Button
            onClick={onBack}
            variant="ghost"
            size="icon"
            className="text-muted-foreground hover:text-foreground hover:bg-accent"
            aria-label={t('JoinClass.detailBack')}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            {/* Subject as the headline, class name as the pill — same ordering as the tile that opens this
                page, so the two do not disagree about what this class is called. */}
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-bold text-foreground tracking-tight">
                {classroom.subject ? subjectLabel(classroom.subject) : classroom.name}
              </h1>
              {classroom.subject && (
                <span className="px-2 py-0.5 bg-brand-surface text-brand-text border border-brand-border rounded text-xs font-medium">
                  {classroom.name}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="flex-1 px-4 sm:px-8 py-8">
        <div className="max-w-6xl mx-auto space-y-6 animate-in fade-in duration-500">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {stats.map(({ key, Icon, value }) => (
              <div key={key} className="flex items-center gap-3 p-4 bg-card border border-border rounded-lg shadow-sm">
                <div className="w-10 h-10 bg-brand-surface rounded-lg flex items-center justify-center flex-shrink-0">
                  <Icon className="h-5 w-5 text-brand-text" />
                </div>
                <div>
                  <p className="text-xl font-semibold text-foreground">{value}</p>
                  <p className="text-sm text-muted-foreground">{t(`JoinClass.${key}`)}</p>
                </div>
              </div>
            ))}
          </div>

          <section className="bg-card rounded-xl p-6 sm:p-8 border border-border shadow-sm">
            <h2 className="text-lg font-semibold text-foreground mb-6 flex items-center gap-2">
              <BookOpen className="h-5 w-5 text-brand-text" /> {t('JoinClass.examListTitle')}
            </h2>

            {isLoading ? (
              <p className="text-sm text-muted-foreground">{t('JoinClass.loadingResults')}</p>
            ) : exams.length === 0 ? (
              <p className="text-sm italic text-muted-foreground/70">{t('JoinClass.noExams')}</p>
            ) : (
              <>
                {gradedExams.length === 0 && (
                  <p className="text-sm italic text-muted-foreground/70 mb-4">{t('JoinClass.noResultsHint')}</p>
                )}
                <div className="space-y-2">
                  {exams.map(exam => (
                    <button
                      key={exam.examId}
                      type="button"
                      onClick={() => setOpenExam(exam)}
                      className="w-full flex items-center justify-between gap-3 p-4 bg-background border border-border rounded-lg text-left hover:bg-accent transition-colors"
                    >
                      <div className="min-w-0">
                        <p className="font-medium text-foreground truncate">{exam.title}</p>
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          {exam.subject && (
                            <span className="px-2 py-0.5 bg-brand-surface text-brand-text border border-brand-border rounded text-xs font-medium">
                              {subjectLabel(exam.subject)}
                            </span>
                          )}
                          {exam.percentage != null && (
                            <span className="px-2 py-0.5 bg-amber-50 text-amber-700 rounded text-xs font-medium">
                              {exam.percentage.toFixed(0)}%
                            </span>
                          )}
                          {exam.createdAt && (
                            <span className="flex items-center gap-1 text-xs text-muted-foreground">
                              <Clock className="h-3 w-3" /> {formatDate(exam.createdAt)}
                            </span>
                          )}
                        </div>
                      </div>
                      <span
                        className={`px-2 py-0.5 rounded text-xs font-medium shrink-0 ${
                          exam.grade != null ? 'bg-green-50 text-green-700' : 'bg-muted text-muted-foreground'
                        }`}
                      >
                        {exam.grade ?? t('JoinClass.notGraded')}
                      </span>
                    </button>
                  ))}
                </div>
              </>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}
